import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, FileText, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: '' });
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, [filter]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.category) params.category = filter.category;
      
      const response = await axios.get(`${API}/invoices`, { params });
      setInvoices(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      utilities: 'bg-blue-500',
      internet_phone: 'bg-cyan-500',
      electronics: 'bg-purple-500',
      personal_care: 'bg-pink-500',
      home_supplies: 'bg-green-500',
      transport: 'bg-yellow-500',
      food_restaurants: 'bg-orange-500',
      medical: 'bg-red-500',
      education: 'bg-indigo-500',
      leisure: 'bg-violet-500',
      professional: 'bg-gray-500',
      other: 'bg-slate-500',
    };
    return colors[category] || 'bg-gray-500';
  };

  const totalAmount = invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Invoices & Receipts</h1>
        <p className="text-muted-foreground">Manage and categorize your financial documents</p>
      </div>

      {/* Summary */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{invoices.length}</div>
            <p className="text-sm text-muted-foreground">Total Invoices</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">${totalAmount.toFixed(2)}</div>
            <p className="text-sm text-muted-foreground">Total Amount</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <Select value={filter.category} onValueChange={(v) => setFilter({ ...filter, category: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Categories</SelectItem>
                <SelectItem value="utilities">Utilities</SelectItem>
                <SelectItem value="internet_phone">Internet & Phone</SelectItem>
                <SelectItem value="electronics">Electronics</SelectItem>
                <SelectItem value="personal_care">Personal Care</SelectItem>
                <SelectItem value="home_supplies">Home Supplies</SelectItem>
                <SelectItem value="transport">Transport</SelectItem>
                <SelectItem value="food_restaurants">Food & Restaurants</SelectItem>
                <SelectItem value="medical">Medical</SelectItem>
                <SelectItem value="education">Education</SelectItem>
                <SelectItem value="leisure">Leisure</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Invoice List */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : invoices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No invoices found. Sync emails to detect invoices automatically.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {invoices.map((invoice) => (
            <Card key={invoice.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getCategoryColor(invoice.category)}>
                        {invoice.category.replace('_', ' ')}
                      </Badge>
                      {invoice.subcategory && (
                        <Badge variant="outline">{invoice.subcategory}</Badge>
                      )}
                    </div>
                    <h3 className="font-semibold">{invoice.supplier || 'Unknown Supplier'}</h3>
                    <div className="mt-2 flex items-center gap-4 text-sm">
                      {invoice.amount && (
                        <span className="font-bold">
                          {invoice.currency} {invoice.amount.toFixed(2)}
                        </span>
                      )}
                      {invoice.date && (
                        <span className="text-muted-foreground">
                          {new Date(invoice.date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Invoices;